// activityTracker.ts - Multi-Input Activity Detection for Data Analysis App
import { sessionManager } from './sessionManager'

// 🎯 Activity detection configuration
const ACTIVITY_CONFIG = {
  // Debounce settings
  debounceMs: 2000, // 2 seconds - don't spam activity records
  mouseDebounceMs: 5000, // 5 seconds for mouse movement (less important)

  // Tracking settings
  trackMouse: true, // Mouse movements
  trackClicks: true, // Click events
  trackKeyboard: true, // Keyboard input
  trackScroll: true, // Scrolling
  trackApiCalls: true, // API requests (SQL queries, etc.)
  trackVisibility: true, // Page visibility changes

  // Event types to track
  mouseEvents: ['click', 'mousedown', 'contextmenu'] as const,
  keyboardEvents: ['keydown', 'keypress'] as const,
  scrollEvents: ['scroll', 'wheel'] as const,
  touchEvents: ['touchstart', 'touchmove'] as const,

  // Excluded elements (to avoid noise)
  excludeSelectors: ['[data-activity-ignore]', '.tooltip', '.dropdown-menu'],
} as const

/**
 * Activity Tracker - Monitors user interactions across multiple input methods
 * Optimized for data analysis workflows with minimal performance impact
 */
export class ActivityTracker {
  private isActive = false
  private lastActivityTime = 0
  private debounceTimers = new Map<string, number>()
  private apiCallCount = 0

  private readonly DEBUG = process.env.NODE_ENV === 'development'

  constructor() {
    this.log('ActivityTracker initialized')
  }

  /**
   * Start tracking user activity
   */
  start(): void {
    if (this.isActive) {
      this.log('ActivityTracker already active')
      return
    }

    this.isActive = true
    this.setupEventListeners()
    this.setupApiInterception()
    this.setupVisibilityTracking()

    this.log('ActivityTracker started')
  }

  /**
   * Stop tracking user activity
   */
  stop(): void {
    if (!this.isActive) return

    this.isActive = false
    this.removeEventListeners()
    this.clearDebounceTimers()

    this.log('ActivityTracker stopped')
  }

  /**
   * Setup DOM event listeners for user interactions
   */
  private setupEventListeners(): void {
    // Mouse events
    if (ACTIVITY_CONFIG.trackClicks) {
      ACTIVITY_CONFIG.mouseEvents.forEach((event) => {
        document.addEventListener(event, this.handleMouseActivity, {
          passive: true,
          capture: true,
        })
      })
    }

    // Mouse movement (with longer debounce)
    if (ACTIVITY_CONFIG.trackMouse) {
      document.addEventListener('mousemove', this.handleMouseMovement, {
        passive: true,
      })
    }

    // Keyboard events
    if (ACTIVITY_CONFIG.trackKeyboard) {
      ACTIVITY_CONFIG.keyboardEvents.forEach((event) => {
        document.addEventListener(event, this.handleKeyboardActivity, {
          passive: true,
        })
      })
    }

    // Scroll events
    if (ACTIVITY_CONFIG.trackScroll) {
      ACTIVITY_CONFIG.scrollEvents.forEach((event) => {
        document.addEventListener(event, this.handleScrollActivity, {
          passive: true,
        })
      })
    }

    // Touch events (for mobile/tablet support)
    ACTIVITY_CONFIG.touchEvents.forEach((event) => {
      document.addEventListener(event, this.handleTouchActivity, {
        passive: true,
      })
    })
  }

  /**
   * Remove all event listeners
   */
  private removeEventListeners(): void {
    ACTIVITY_CONFIG.mouseEvents.forEach((event) => {
      document.removeEventListener(event, this.handleMouseActivity, true)
    })

    document.removeEventListener('mousemove', this.handleMouseMovement)

    ACTIVITY_CONFIG.keyboardEvents.forEach((event) => {
      document.removeEventListener(event, this.handleKeyboardActivity)
    })

    ACTIVITY_CONFIG.scrollEvents.forEach((event) => {
      document.removeEventListener(event, this.handleScrollActivity)
    })

    ACTIVITY_CONFIG.touchEvents.forEach((event) => {
      document.removeEventListener(event, this.handleTouchActivity)
    })

    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    )
  }

  /**
   * Handle mouse click events
   */
  private handleMouseActivity = (event: Event): void => {
    if (!this.isActive || this.shouldIgnoreElement(event.target as Element))
      return

    this.recordActivity('mouse_click', {
      type: event.type,
      target: this.getElementInfo(event.target as Element),
    })
  }

  /**
   * Handle mouse movement with longer debounce
   */
  private handleMouseMovement = (event: MouseEvent): void => {
    if (!this.isActive) return

    this.recordActivityDebounced(
      'mouse_movement',
      ACTIVITY_CONFIG.mouseDebounceMs,
      {
        x: event.clientX,
        y: event.clientY,
      },
    )
  }

  /**
   * Handle keyboard input
   */
  private handleKeyboardActivity = (event: KeyboardEvent): void => {
    if (!this.isActive) return

    // Ignore modifier keys alone
    if (['Control', 'Alt', 'Shift', 'Meta'].includes(event.key)) return

    this.recordActivity('keyboard', {
      key: event.key.length === 1 ? '[char]' : event.key, // Sanitize for privacy
      type: event.type,
    })
  }

  /**
   * Handle scroll events
   */
  private handleScrollActivity = (): void => {
    if (!this.isActive) return

    this.recordActivityDebounced('scroll', ACTIVITY_CONFIG.debounceMs)
  }

  /**
   * Handle touch events (mobile support)
   */
  private handleTouchActivity = (event: TouchEvent): void => {
    if (!this.isActive) return

    this.recordActivity('touch', {
      type: event.type,
      touches: event.touches.length,
    })
  }

  /**
   * Setup API call interception to track data queries
   */
  private setupApiInterception(): void {
    if (!ACTIVITY_CONFIG.trackApiCalls) return

    // Intercept axios requests through our existing axiosInstance
    // This is done by patching the axios instance used throughout the app
    if (typeof window !== 'undefined' && (window as any).axios) {
      const originalRequest = (window as any).axios.request

      ;(window as any).axios.request = (...args: any[]) => {
        this.recordApiActivity()
        return originalRequest.apply((window as any).axios, args)
      }
    }

    // Also track fetch calls (if used)
    if (typeof window !== 'undefined' && window.fetch) {
      const originalFetch = window.fetch

      window.fetch = (...args: Parameters<typeof fetch>) => {
        this.recordApiActivity()
        return originalFetch.apply(window, args)
      }
    }
  }

  /**
   * Record API call activity
   */
  private recordApiActivity(): void {
    if (!this.isActive) return

    this.apiCallCount++
    this.recordActivity('api_call', {
      count: this.apiCallCount,
      timestamp: Date.now(),
    })
  }

  /**
   * Setup page visibility tracking
   */
  private setupVisibilityTracking(): void {
    if (!ACTIVITY_CONFIG.trackVisibility) return

    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  /**
   * Handle page visibility changes
   */
  private handleVisibilityChange = (): void => {
    if (!this.isActive) return

    const isVisible = document.visibilityState === 'visible'

    this.log('Visibility changed', { visible: isVisible })

    // Record activity when user comes back to the tab
    if (isVisible) {
      this.recordActivity('visibility_return', {
        timestamp: Date.now(),
      })
    }
  }

  /**
   * Record activity with debouncing
   */
  private recordActivityDebounced(
    type: string,
    debounceMs: number,
    data?: any,
  ): void {
    const timerId = this.debounceTimers.get(type)

    if (timerId) {
      clearTimeout(timerId)
    }

    const newTimerId = window.setTimeout(() => {
      this.recordActivity(type, data)
      this.debounceTimers.delete(type)
    }, debounceMs)

    this.debounceTimers.set(type, newTimerId)
  }

  /**
   * Record activity immediately
   */
  private recordActivity(type: string, data?: any): void {
    if (!this.isActive) return

    const now = Date.now()

    // Avoid too frequent activity records
    if (now - this.lastActivityTime < ACTIVITY_CONFIG.debounceMs) {
      return
    }

    this.lastActivityTime = now

    // Notify session manager
    sessionManager.recordActivity()

    this.log('Activity recorded', { type, data })
  }

  /**
   * Check if element should be ignored
   */
  private shouldIgnoreElement(element: Element | null): boolean {
    if (!element) return false

    return ACTIVITY_CONFIG.excludeSelectors.some((selector) => {
      try {
        return element.matches(selector) || element.closest(selector) !== null
      } catch {
        return false
      }
    })
  }

  /**
   * Get safe element information for logging
   */
  private getElementInfo(element: Element | null): object {
    if (!element) return {}

    // Safely handle className which might be SVGAnimatedString or DOMTokenList
    let className: string | undefined
    // Safely handle className which might be SVGAnimatedString or DOMTokenList
    if (element.className) {
      // Handle different className types
      if (typeof element.className === 'string') {
        className = element.className.split(' ')[0] || undefined
      } else if ('baseVal' in element.className) {
        // SVGAnimatedString case
        className =
          (element.className as any).baseVal?.split(' ')[0] || undefined
      } else {
        // DOMTokenList or other cases
        className = String(element.className).split(' ')[0] || undefined
      }
    }

    return {
      tagName: element.tagName.toLowerCase(),
      className,
      id: element.id || undefined,
      type: (element as HTMLInputElement).type || undefined,
    }
  }

  /**
   * Clear all debounce timers
   */
  private clearDebounceTimers(): void {
    this.debounceTimers.forEach((timerId) => clearTimeout(timerId))
    this.debounceTimers.clear()
  }

  /**
   * Get activity statistics
   */
  getStats(): {
    isActive: boolean
    lastActivityTime: number
    apiCallCount: number
    timeSinceLastActivity: number
  } {
    return {
      isActive: this.isActive,
      lastActivityTime: this.lastActivityTime,
      apiCallCount: this.apiCallCount,
      timeSinceLastActivity: Date.now() - this.lastActivityTime,
    }
  }

  /**
   * Manual activity trigger (for programmatic use)
   */
  triggerActivity(source: string = 'manual'): void {
    this.recordActivity('manual_trigger', { source })
  }

  /**
   * Debug logging
   */
  private log(message: string, data?: any): void {
    if (this.DEBUG) {
      console.log(`[ActivityTracker] ${message}`, data || '')
    }
  }
}

// Export singleton instance
export const activityTracker = new ActivityTracker()
